import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiDatingPhotoGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator',
			name: 'AI Dating Photo Generator — Get Better Dating Profile Photos | Lift',
			description:
				'Look your best on Tinder, Hinge & Bumble — instantly with AI. No watermark. Upload photo, create perfect dating profile images in multiple styles. Try now!',
			url: 'https://liftapp.ai/ai-dating-photo-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-dating-photo-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#product',
			name: 'AI Dating Photo Generator',
			description:
				'Create professional dating app photos in minutes. Get studio-quality Tinder, Hinge & Bumble profile images with AI, no watermarks.',
			url: 'https://liftapp.ai/ai-dating-photo-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional dating photos automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#benefits',
			name: 'Who can benefit from AI Dating Photo Generator?',
			description:
				"Lift's AI Dating Photo Generator is useful for professionals, creators, and businesses who want clean, distraction-free images for social media, e-commerce, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Create professional LinkedIn headshots that double as polished dating profile photos. Stand out with high-quality images that build trust and attract meaningful connections.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Generate multiple professional headshots for business and personal use without expensive photo shoots. Perfect for Tinder, Bumble, and professional networks alike.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Remote Teams — Professionals working remotely can quickly create consistent, high-quality dating photos without scheduling studio sessions. Get camera-ready results from home in minutes.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your dating app profiles with AI-generated photos that match your authentic style. Experiment with different looks and backgrounds to find what works best.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create confident, professional-looking dating profile pictures on a budget. No need for expensive photographers — just upload selfies and get studio-quality results instantly.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI dating profile picture use cases',
				description: 'AI dating profile picture use cases',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Dating Photo Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Dating Photo Generator is a premium online tool that instantly creates professional dating app photos with studio-quality results — ideal for Tinder, Bumble, Hinge, and other dating platforms.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Dating Photo Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI dating photo generator. Our AI automatically creates professional dating profile pictures in various styles and backgrounds in minutes — no photo shoot needed. Generate multiple dating app photos with different outfits and settings.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Dating Photo Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#why-lift',
			name: "Why Lift's AI Dating Photo Generator stands out",
			description:
				"Lift's Dating Photo Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI dating photos',
				description: 'AI dating photos',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-dating-photo-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Dating Photo Generator',
					item: 'https://liftapp.ai/ai-dating-photo-generator',
				},
			],
		},
	],
};
